// Sign in, create an account, forgot password, and the new-password form a reset link opens.
import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { config, isDemo } from '../config';
import { useI18n } from '../i18n/I18nProvider';
import { db } from '../lib/db';
import { openedForRecovery } from '../lib/initialUrl';
import { useAuth } from './AuthProvider';

type Form = 'in' | 'up' | 'forgot' | 'new';
interface Msg {
  text: string;
  kind: '' | 'ok' | 'err';
}

/** A password field with an eye button that shows or hides what was typed. */
function PasswordInput(props: { autoComplete: string; minLength?: number; value: string; onChange(v: string): void }) {
  const { t } = useI18n();
  const [shown, setShown] = useState(false);
  return (
    <span className="pw">
      <input
        type={shown ? 'text' : 'password'}
        autoComplete={props.autoComplete}
        minLength={props.minLength}
        required
        spellCheck={false}
        autoCapitalize="none"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
      />
      <button
        type="button"
        className="pw-eye"
        aria-label={t(shown ? 'lgPwHide' : 'lgPwShow')}
        aria-pressed={shown}
        title={t(shown ? 'lgPwHide' : 'lgPwShow')}
        onClick={(e) => {
          e.preventDefault(); // inside a <label>: keep the click on the button
          setShown(!shown);
        }}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
          <circle cx="12" cy="12" r="3" />
          {shown && <path d="M3 3l18 18" />}
        </svg>
      </button>
    </span>
  );
}

function safeNext(v: string | null): string {
  return v && /^\/[\w-]*$/.test(v) && v !== '/login' ? v : '/';
}

export function LoginPage() {
  const { t, lang, setLang } = useI18n();
  const auth = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const [form, setForm] = useState<Form>(openedForRecovery ? 'new' : 'in');
  const [recovering, setRecovering] = useState(openedForRecovery);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>({ text: '', kind: '' });
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    document.title = t('lgIn') + ' · ' + config.siteName;
  }, [t]);
  useEffect(
    () =>
      db.onAuth((ev) => {
        if (ev === 'PASSWORD_RECOVERY') {
          setRecovering(true);
          setForm('new');
        }
      }),
    [],
  );

  if (isDemo) return <Navigate to={next} replace />;
  if (!recovering && (auth.status === 'ready' || auth.status === 'pending')) return <Navigate to={next} replace />;

  const show = (f: Form) => {
    setForm(f);
    setMsg({ text: '', kind: '' });
  };
  const fail = (err: unknown) => {
    let m = String((err as Error)?.message || err);
    if (/invalid login credentials/i.test(m)) m = t('lgBad');
    else if (/email not confirmed/i.test(m)) m = t('lgUnconfirmed');
    setMsg({ text: m, kind: 'err' });
    setBusy(false);
  };
  const start = () => {
    setBusy(true);
    setMsg({ text: t('lgWorking'), kind: '' });
  };

  const onSignIn = async (e: FormEvent) => {
    e.preventDefault();
    start();
    try {
      await db.signIn(email.trim(), pw);
      await auth.refresh();
      navigate(next, { replace: true });
    } catch (err) {
      fail(err);
    }
  };
  const onSignUp = async (e: FormEvent) => {
    e.preventDefault();
    start();
    try {
      const r = await db.signUp(email.trim(), pw, name.trim());
      if (r.needsConfirm) {
        setMsg({ text: t('lgConfirm'), kind: 'ok' });
        setBusy(false);
      } else {
        await auth.refresh();
        navigate(next, { replace: true });
      }
    } catch (err) {
      fail(err);
    }
  };
  const onForgot = async (e: FormEvent) => {
    e.preventDefault();
    start();
    try {
      await db.resetPassword(email.trim());
      setMsg({ text: t('lgSent'), kind: 'ok' });
      setBusy(false);
    } catch (err) {
      fail(err);
    }
  };
  const onNewPw = async (e: FormEvent) => {
    e.preventDefault();
    start();
    try {
      await db.updatePassword(pw);
      setMsg({ text: t('lgPwSaved'), kind: 'ok' });
      setTimeout(() => {
        setRecovering(false);
        void auth.refresh().then(() => navigate(next, { replace: true }));
      }, 900);
    } catch (err) {
      fail(err);
    }
  };

  const status = (
    <div className={'msg ' + msg.kind} role="status">
      {msg.text}
    </div>
  );
  return (
    <div className="pg">
      <div className="login-top">
        <div className="uco-lang">
          <button className={lang === 'en' ? 'on' : ''} onClick={() => setLang('en')}>
            EN
          </button>
          <button className={lang === 'fr' ? 'on' : ''} onClick={() => setLang('fr')}>
            FR
          </button>
        </div>
      </div>
      <div className="login-wrap">
        <div className="login-card">
          <div className="brand">
            <span className="uco-mark">◆</span>
            <span>{config.siteName}</span>
          </div>
          <div className="sub">{t('lgTag')}</div>

          {(form === 'in' || form === 'up') && (
            <div className="tabs">
              <button className={form === 'in' ? 'on' : ''} onClick={() => show('in')}>
                {t('lgIn')}
              </button>
              <button className={form === 'up' ? 'on' : ''} onClick={() => show('up')}>
                {t('lgUp')}
              </button>
            </div>
          )}

          {form === 'in' && (
            <form onSubmit={onSignIn} autoComplete="on">
              <label className="f">
                <span>{t('lgEmail')}</span>
                <input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </label>
              <label className="f">
                <span>{t('lgPw')}</span>
                <PasswordInput autoComplete="current-password" value={pw} onChange={setPw} />
              </label>
              <button className="primary" type="submit" disabled={busy}>
                {t('lgIn')}
              </button>
              <button type="button" className="link" onClick={() => show('forgot')}>
                {t('lgForgot')}
              </button>
              {status}
            </form>
          )}

          {form === 'up' && (
            <form onSubmit={onSignUp} autoComplete="on">
              <label className="f">
                <span>{t('lgName')}</span>
                <input type="text" autoComplete="name" required value={name} onChange={(e) => setName(e.target.value)} />
              </label>
              <label className="f">
                <span>{t('lgEmail')}</span>
                <input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </label>
              <label className="f">
                <span>{t('lgPwNew')}</span>
                <PasswordInput autoComplete="new-password" minLength={8} value={pw} onChange={setPw} />
              </label>
              <button className="primary" type="submit" disabled={busy}>
                {t('lgUp')}
              </button>
              <div className="note">{t('lgUpNote')}</div>
              {status}
            </form>
          )}

          {form === 'forgot' && (
            <form onSubmit={onForgot}>
              <div className="note">{t('lgForgotNote')}</div>
              <label className="f">
                <span>{t('lgEmail')}</span>
                <input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </label>
              <button className="primary" type="submit" disabled={busy}>
                {t('lgSend')}
              </button>
              <button type="button" className="link" onClick={() => show('in')}>
                {t('lgBack')}
              </button>
              {status}
            </form>
          )}

          {form === 'new' && (
            <form onSubmit={onNewPw}>
              <div className="note">{t('lgNewNote')}</div>
              <label className="f">
                <span>{t('lgPwNew')}</span>
                <PasswordInput autoComplete="new-password" minLength={8} value={pw} onChange={setPw} />
              </label>
              <button className="primary" type="submit" disabled={busy}>
                {t('lgSetPw')}
              </button>
              {status}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
